import axios, { type AxiosError } from "axios";
import * as qs from "qs";
import type { ApiError } from "./shared";
import { useAuthStore } from "../service/useAuthStore";
import { ENDPOINTS } from "./authEndpoints";

const apiClient = axios.create({
  baseURL: "",
  paramsSerializer: (params) => {
    return qs.stringify(params, { arrayFormat: "comma" });
  },
});

apiClient.defaults.headers.common.Accept = "application/json";
apiClient.defaults.headers.post["Content-Type"] = "application/json";

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token!);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<unknown>) => {
    const originalRequest = error.config as typeof error.config & {
      _retry?: boolean;
    };

    if (
      error.response?.status === 401 &&
      !originalRequest?._retry &&
      originalRequest?.url !== ENDPOINTS.login
    ) {
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          if (originalRequest) {
            originalRequest.headers!.Authorization = `Bearer ${token}`;
          }
          return apiClient(originalRequest!);
        });
      }

      originalRequest!._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem("refreshToken");

      if (!refreshToken) {
        isRefreshing = false;
        useAuthStore.getState().logout();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(ENDPOINTS.refresh, {
          refreshToken,
        });
        const { accessToken, refreshToken: newRefreshToken } = response.data;

        useAuthStore.getState().setAccessToken(accessToken);
        if (newRefreshToken) {
          useAuthStore.getState().setRefreshToken(newRefreshToken);
        }

        processQueue(null, accessToken);
        originalRequest!.headers!.Authorization = `Bearer ${accessToken}`;
        return apiClient(originalRequest!);
      } catch (refreshError) {
        processQueue(refreshError, null);
        useAuthStore.getState().logout();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    let apiError: ApiError;
    const status = error.response?.status;
    const hasBody =
      error.response?.data &&
      typeof error.response.data === "object" &&
      Object.keys(error.response.data as object).length > 0;

    if (!error.response || status === 502 || status === 503 || status === 504) {
      apiError = {
        code: "network.error",
        message: error.message,
        error: "network.error",
      };
    } else if (status === 500) {
      apiError = {
        code: "internal.server.error",
        message: error.message,
        error: "internal.server.error",
      };
    } else if (status === 403) {
      apiError = {
        code: "forbidden",
        message: error.message,
        error: "forbidden",
      };
    } else if (hasBody) {
      apiError = error.response.data as ApiError;
    } else {
      apiError = {
        code: "network.error",
        message: error.message,
        error: "network.error",
      };
    }
    return Promise.reject(apiError);
  },
);

export default apiClient;
